import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, RefreshCw, Loader2, Package, History } from "lucide-react";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { ProductCard } from "../components/ProductCard";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { clientApi } from "../lib/api";
import { useIncrementalList } from "../lib/useIncrementalList";
import { toast } from "sonner";

const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 1) return "Ayer";
    if (days < 7) return `Hace ${days} días`;
    if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

export default function MySuggestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { visible, hasMore, sentinelRef } = useIncrementalList(items, { step: 36 });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.get("/portal/suggestions");
      setItems(data || []);
    } catch (err) {
      toast.error("No pudimos cargar tus sugerencias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await clientApi.get("/portal/suggestions");
        if (!cancelled) setItems(data || []);
      } catch (err) {
        if (!cancelled) toast.error("No pudimos cargar tus sugerencias");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="my-suggestions-page">
      <Header />
      <CartSheet />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/catalogo"
          className="inline-flex items-center text-sm text-cm-ink-soft hover:text-cm-red mb-4"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver al catálogo
        </Link>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
              Basado en tu historial
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight flex items-center gap-3">
              <Sparkles className="h-7 w-7 text-cm-yellow fill-cm-yellow" />
              Sugeridos para ti
            </h1>
            <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
              Productos que ya has comprado antes, ordenados por frecuencia. Pídelos de nuevo con un clic.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchItems}
            className="border-cm-border"
            data-testid="refresh-suggestions-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>

        {loading ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
            <p className="text-sm text-cm-ink-soft mt-3">Cargando tus sugerencias...</p>
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Package className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
            <h2 className="font-serif text-xl mt-4">Aún no tienes sugerencias</h2>
            <p className="text-sm text-cm-ink-soft mt-2 mb-6 max-w-md mx-auto">
              Cuando hagas tu primer pedido, aquí aparecerán los productos que más compras para que los repitas rápido.
            </p>
            <Button asChild className="btn-cm-primary" data-testid="empty-suggestions-go-catalog">
              <Link to="/catalogo">Ir al catálogo</Link>
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {visible.map((p) => (
              <div key={p.codigo} className="relative" data-testid={`suggestion-${p.codigo}`}>
                <Badge
                  className="absolute z-10 -top-2 -left-2 bg-cm-red text-white hover:bg-cm-red text-[10px] shadow"
                  data-testid={`suggestion-stats-${p.codigo}`}
                >
                  <History className="h-3 w-3 mr-1" />
                  {p.veces_pedido} pedido{p.veces_pedido === 1 ? "" : "s"} · {p.total_cantidad_historica} und
                </Badge>
                <ProductCard product={p} />
                {p.ultima_fecha && (
                  <p className="text-[10px] text-cm-ink-soft mt-1.5 text-center font-mono">
                    Última vez: {formatRelative(p.ultima_fecha)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && hasMore && (
          <div ref={sentinelRef} className="py-10 flex justify-center" data-testid="suggestions-load-more">
            <Loader2 className="h-5 w-5 animate-spin text-cm-ink-soft" />
          </div>
        )}
      </main>
    </div>
  );
}
